<template>
  <Layout>
    <div class="reports-page">
      <section class="hero-panel">
        <div class="hero-copy">
          <div class="hero-tag">Report Center</div>
          <h2>{{ activeCustomer.name }}投放全景</h2>
          <p>聚合所有广告计划的展示/浏览、点击/互动与转化表现，帮助运营团队快速发现高效计划与异常波动。</p>
        </div>
        <div class="hero-meta">
          <div class="meta-pill">
            <span class="meta-label">观察周期</span>
            <div class="period-switcher">
              <button
                v-for="option in periodOptions"
                :key="option.value"
                type="button"
                :class="['period-button', { active: activePeriod === option.value }]"
                @click="activePeriod = option.value">
                {{ option.label }}
              </button>
            </div>
          </div>
          <div class="meta-pill">
            <span class="meta-label">统计范围</span>
            <strong>{{ reportRows.length }} 个计划</strong>
          </div>
        </div>
      </section>

      <el-row :gutter="20" class="summary-grid">
        <el-col :span="8" v-for="metric in summaryMetrics" :key="metric.label">
          <div class="metric-card">
            <span class="metric-label">{{ metric.label }}</span>
            <div class="metric-value">{{ metric.value }}</div>
            <span class="metric-footnote">{{ metric.footnote }}</span>
          </div>
        </el-col>
      </el-row>

      <el-card shadow="never" class="report-card trend-card">
        <div slot="header" class="section-header">
          <div>
            <h3>{{ activeTrend.title || '趋势表现' }}</h3>
            <p>{{ customerFocusText }}</p>
          </div>
        </div>

        <div class="trend-grid">
          <div v-for="card in trendCards" :key="card.key" class="trend-panel">
            <div
              v-if="activeTrendTooltip.visible && activeTrendTooltip.cardKey === card.key"
              class="trend-tooltip"
              :style="trendTooltipStyle">
              <span class="tooltip-label" :style="{ color: activeTrendTooltip.color }">{{ activeTrendTooltip.label }}</span>
              <strong>{{ activeTrendTooltip.value }}</strong>
              <span class="tooltip-date">{{ activeTrendTooltip.date }}</span>
            </div>
            <div class="trend-head">
              <div>
                <span class="trend-label">{{ card.label }}</span>
                <strong>{{ card.latest }}</strong>
              </div>
              <span class="trend-badge" :style="{ color: card.color, backgroundColor: card.badgeBackground }">
                {{ card.changeLabel }}
              </span>
            </div>

            <svg class="trend-svg" viewBox="0 0 360 168" preserveAspectRatio="none">
              <defs>
                <linearGradient :id="`gradient-${card.key}`" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" :stop-color="card.fillColor" stop-opacity="0.32" />
                  <stop offset="100%" :stop-color="card.fillColor" stop-opacity="0.02" />
                </linearGradient>
              </defs>
              <path class="trend-grid-line" d="M 0 144 L 360 144" />
              <path class="trend-grid-line" d="M 0 96 L 360 96" />
              <path class="trend-grid-line" d="M 0 48 L 360 48" />
              <path
                class="trend-area"
                :d="card.areaPath"
                :fill="`url(#gradient-${card.key})`" />
              <polyline
                class="trend-line"
                :points="card.points"
                :stroke="card.color" />
              <circle
                v-for="(point, index) in card.dotPoints"
                :key="`${card.key}-${index}`"
                :cx="point.x"
                :cy="point.y"
                r="4"
                :fill="card.color"
                @mouseenter="showTrendTooltip(card, point, index)"
                @mouseleave="hideTrendTooltip"
                class="trend-dot" />
            </svg>

            <div class="trend-axis" :class="{ 'single-point': trendDays.length <= 1 }">
              <span v-for="day in trendDays" :key="`${card.key}-${day.date}`">{{ day.date }}</span>
            </div>
          </div>
        </div>
      </el-card>

      <el-row :gutter="20" class="content-grid">
        <el-col :xl="18" :lg="16" :md="24" :sm="24" :xs="24" class="content-main-col">
          <el-card shadow="never" class="report-card">
            <div slot="header" class="section-header">
              <div class="section-copy">
                <h3>计划表现总览</h3>
                <p>按计划查看展示/浏览、点击/互动、转化和点击/互动率，并支持表头排序。</p>
              </div>
              <div class="table-controls">
                <div class="search-box">
                  <input
                    v-model.trim="searchKeyword"
                    type="text"
                    class="search-input"
                    placeholder="搜索广告计划" />
                </div>
                <div class="filter-switcher">
                  <button
                    v-for="option in filterOptions"
                    :key="option.value"
                    type="button"
                    :class="['filter-button', { active: activeFilter === option.value }]"
                    @click="activeFilter = option.value">
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>

            <el-table :data="pagedReportRows" stripe class="report-table" @sort-change="handleSortChange" style="width: 100%">
              <el-table-column prop="name" label="广告计划" width="300" show-overflow-tooltip />
              <el-table-column prop="activityName" label="广告活动" width="150" show-overflow-tooltip />
              <el-table-column prop="campaignTypeText" label="类型" width="110">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.campaignType === 'article' ? 'warning' : 'success'">
                    {{ scope.row.campaignTypeText }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="statusText" label="状态" width="96">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.statusTag">{{ scope.row.statusText }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="impressions" label="展示/浏览" width="120" sortable="custom" column-key="impressions" />
              <el-table-column prop="clicks" label="点击/互动" width="110" sortable="custom" column-key="clicks" />
              <el-table-column prop="conversions" label="转化" width="110" sortable="custom" column-key="conversions" />
              <el-table-column prop="ctr" label="点击/互动率" width="138" sortable="custom" column-key="ctr" />
              <el-table-column label="操作" width="48">
                <template slot-scope="scope">
                  <el-tooltip content="查看" placement="top">
                    <el-button
                      type="text"
                      size="small"
                      class="action-icon-button"
                      @click="viewCampaignDetail(scope.row)">
                      <i class="el-icon-view"></i>
                    </el-button>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>

            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="currentPage"
                :page-sizes="[10, 30, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="totalFilteredReportRows">
              </el-pagination>
            </div>
          </el-card>
        </el-col>
        <el-col :xl="6" :lg="8" :md="24" :sm="24" :xs="24" class="content-side-col">
          <el-card shadow="never" class="report-card insight-card">
            <div slot="header" class="section-header compact">
              <div>
                <h3>运营观察</h3>
                <p>优先关注高价值和异常计划。</p>
              </div>
            </div>

            <div class="insight-list">
              <div class="insight-item">
                <span class="insight-kicker">Top 点击/互动率</span>
                <strong>{{ topCtrCampaign.name }}</strong>
                <p>{{ topCtrCampaign.activityName || '未分配活动' }} · {{ topCtrCampaign.campaignTypeText }} 点击/互动率 {{ topCtrCampaign.ctr }}，说明创意与投放渠道匹配较好。</p>
              </div>
              <div class="insight-item">
                <span class="insight-kicker">Top 转化</span>
                <strong>{{ topConversionCampaign.name }}</strong>
                <p>{{ topConversionCampaign.activityName || '未分配活动' }} · {{ topConversionCampaign.campaignTypeText }}累计转化 {{ topConversionCampaign.conversions }}，建议继续保持当前素材方向。</p>
              </div>
              <div class="insight-item warning">
                <span class="insight-kicker">异常提醒</span>
                <strong>{{ warningCampaign.name }}</strong>
                <p>点击/互动活跃但转化偏弱，建议检查落地页承接和定向策略。</p>
              </div>
            </div>
          </el-card>

          <el-card shadow="never" class="report-card rank-card">
            <div slot="header" class="section-header compact">
              <div>
                <h3>点击/互动率 排行</h3>
                <p>高点击/互动率计划一览。</p>
              </div>
            </div>

            <div class="rank-list">
              <div v-for="(item, index) in ctrRanking" :key="item.id" class="rank-item">
                <div class="rank-index">{{ index + 1 }}</div>
                <div class="rank-content">
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.ctr }} · {{ item.clicks }} 点击/互动</span>
                </div>
                <div class="rank-value">{{ item.conversions }} 转化</div>
              </div>
            </div>
          </el-card>

          <el-card v-if="showActivityConversionLeads" shadow="never" class="report-card leads-card">
            <div slot="header" class="section-header compact">
              <div>
                <h3>活动转化线索</h3>
                <p>按活动下载该活动下全部广告的咨询名单，至少包含姓名、电话和城市。</p>
              </div>
            </div>

            <div v-if="activityLeadExports.length" class="lead-export-list">
              <div v-for="item in activityLeadExports" :key="item.activityId" class="lead-export-item">
                <div class="lead-export-content">
                  <strong>{{ item.activityName }}</strong>
                  <span>{{ item.campaignCount }} 个计划 · {{ item.conversionsText }}</span>
                </div>
                <button type="button" class="lead-export-button" @click="downloadActivityConversionLeads(item)">
                  下载转化线索
                </button>
              </div>
            </div>
            <div v-else class="empty-state">
              暂无可导出的活动线索
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'ReportsCenter',
  components: {
    Layout
  },
  data() {
    return {
      activePeriod: 'all',
      activeSort: 'campaignDate',
      sortOrder: 'descending',
      activeFilter: 'all',
      searchKeyword: '',
      currentPage: 1,
      pageSize: 30,
      activeTrendTooltip: {
        visible: false,
        cardKey: '',
        label: '',
        value: '',
        date: '',
        x: 0,
        y: 0,
        color: ''
      },
      periodOptions: [
        { label: '天', value: 'day' },
        { label: '周', value: 'week' },
        { label: '所有', value: 'all' }
      ],
      filterOptions: [
        { label: '全部计划', value: 'all' },
        { label: '只看运行中', value: 'running' },
        { label: '高展示/浏览', value: 'highImpression' },
        { label: '高转化', value: 'highConversion' }
      ]
    }
  },
  computed: {
    ...mapState(['reports']),
    ...mapGetters(['activeCustomer']),
    reportPeriods() {
      return this.reports.periods || {}
    },
    activeReport() {
      return this.reportPeriods[this.activePeriod] || this.reportPeriods.all || this.reportPeriods.day || {
        rows: [],
        summary: {},
        trend: { days: [] }
      }
    },
    reportRows() {
      return this.activeReport.rows || this.reports.rows || []
    },
    filteredReportRows() {
      let rows = this.reportRows
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        rows = rows.filter(row =>
          (row.name || '').toLowerCase().includes(keyword) ||
          (row.activityName || '').toLowerCase().includes(keyword)
        )
      }
      if (this.activeFilter === 'all') {
        return rows
      }
      if (this.activeFilter === 'running') {
        return rows.filter(row => row.statusText === '运行中')
      }
      if (this.activeFilter === 'highImpression') {
        const threshold = this.getTopThreshold(rows, 'rawImpressions', 0.35)
        return rows.filter(row => row.rawImpressions >= threshold)
      }
      if (this.activeFilter === 'highConversion') {
        const threshold = this.getTopThreshold(rows, 'rawConversions', 0.35)
        return rows.filter(row => row.rawConversions >= threshold)
      }
      return rows
    },
    sortedReportRows() {
      const rows = [...this.filteredReportRows]
      const sorters = {
        campaignDate: (a, b) => {
          const aTime = new Date(a.startDate || a.endDate || 0).getTime()
          const bTime = new Date(b.startDate || b.endDate || 0).getTime()
          return bTime - aTime
        },
        impressions: (a, b) => b.rawImpressions - a.rawImpressions,
        clicks: (a, b) => b.rawClicks - a.rawClicks,
        ctr: (a, b) => parseFloat(b.ctr) - parseFloat(a.ctr),
        conversions: (a, b) => b.rawConversions - a.rawConversions
      }
      const sorter = sorters[this.activeSort] || sorters.campaignDate
      const sorted = rows.sort(sorter)
      return this.sortOrder === 'ascending' ? sorted.reverse() : sorted
    },
    pagedReportRows() {
      const startIndex = (this.currentPage - 1) * this.pageSize
      return this.sortedReportRows.slice(startIndex, startIndex + this.pageSize)
    },
    totalFilteredReportRows() {
      return this.sortedReportRows.length
    },
    activeSummary() {
      return this.activeReport.summary || this.reports.summary || {}
    },
    activeTrend() {
      return this.activeReport.trend || this.reports.trend || { days: [] }
    },
    trendDays() {
      return this.activeTrend.days || []
    },
    summaryMetrics() {
      const summary = this.activeSummary
      return [
        { label: '总展示/浏览', value: summary.impressions || '0', footnote: summary.footnote || '覆盖所有广告计划' },
        { label: '总点击/互动', value: summary.clicks || '0', footnote: `整体 点击/互动率 ${summary.ctr || '0.00%'}` },
        { label: '总转化', value: summary.conversions || '0', footnote: `整体 CVR ${summary.cvr || '0.00%'}` }
      ]
    },
    periodLabel() {
      return (this.periodOptions.find(option => option.value === this.activePeriod) || {}).label || '天'
    },
    periodFocusLabel() {
      return this.activePeriod === 'all' ? '全周期' : `按${this.periodLabel}`
    },
    customerFocusText() {
      const automotiveCustomers = ['dongfeng_nissan', 'changan_auto', 'bmw', 'gac_trumpchi', 'mercedes_benz', 'lexus_auto', 'li_auto']
      if (automotiveCustomers.includes(this.activeCustomer.id)) {
        return `重点观察试驾招募、到店线索、区域门店导流等计划在${this.periodFocusLabel}视角下的放量节奏和成本稳定性。`
      }
      return `重点观察拉新拓客、复购召回、活动大促等计划在${this.periodFocusLabel}视角下的流量峰值和成交转化效率。`
    },
    topCtrCampaign() {
      return [...this.reportRows].sort((a, b) => parseFloat(b.ctr) - parseFloat(a.ctr))[0] || this.emptyCampaignState
    },
    topConversionCampaign() {
      return [...this.reportRows].sort((a, b) => b.rawConversions - a.rawConversions)[0] || this.emptyCampaignState
    },
    warningCampaign() {
      return [...this.reportRows].sort((a, b) => b.completionPercent - a.completionPercent)[0] || this.emptyCampaignState
    },
    showActivityConversionLeads() {
      return !this.$root || !this.$root.appConfig
        ? true
        : this.$root.appConfig.showActivityConversionLeads !== false
    },
    ctrRanking() {
      return [...this.reportRows]
        .sort((a, b) => parseFloat(b.ctr) - parseFloat(a.ctr))
        .slice(0, 4)
    },
    activityLeadExports() {
      const sourceRows = ((this.reportPeriods.all || {}).rows) || this.reportRows
      const activityMap = {}
      sourceRows.forEach(row => {
        if (!row.activityId) {
          return
        }
        if (!activityMap[row.activityId]) {
          activityMap[row.activityId] = {
            activityId: row.activityId,
            activityName: row.activityName || '未分配活动',
            campaignCount: 0,
            rawConversions: 0
          }
        }
        activityMap[row.activityId].campaignCount += 1
        activityMap[row.activityId].rawConversions += Number(row.rawConversions || row.conversions || 0)
      })
      return Object.values(activityMap)
        .map(item => ({
          ...item,
          conversionsText: `${item.rawConversions.toLocaleString()} 转化`
        }))
        .sort((a, b) => b.rawConversions - a.rawConversions)
    },
    emptyCampaignState() {
      return {
        name: '暂无计划',
        activityName: '未分配活动',
        campaignType: 'standard',
        campaignTypeText: '标准广告',
        ctr: '0.00%',
        conversions: 0,
        completionRate: '0%'
      }
    },
    trendCards() {
      return [
        this.buildTrendCard('impressions', '展示/浏览趋势', '#2f6bff', 'rgba(47, 107, 255, 0.16)'),
        this.buildTrendCard('clicks', '点击/互动趋势', '#00a870', 'rgba(0, 168, 112, 0.16)')
      ]
    },
    trendTooltipStyle() {
      return {
        left: `${this.activeTrendTooltip.x}%`,
        top: `${this.activeTrendTooltip.y}px`
      }
    }
  },
  created() {
    this.$store.dispatch('fetchReports')
  },
  watch: {
    reports: {
      immediate: true,
      deep: true,
      handler(nextReports) {
        const periods = (nextReports && nextReports.periods) || {}
        if (!periods[this.activePeriod]) {
          this.activePeriod = periods.all ? 'all' : (periods.day ? 'day' : Object.keys(periods)[0] || 'all')
        }
      }
    },
    activePeriod() {
      this.currentPage = 1
    },
    activeFilter() {
      this.currentPage = 1
    },
    searchKeyword() {
      this.currentPage = 1
    },
    totalFilteredReportRows(total) {
      const maxPage = Math.max(1, Math.ceil(total / this.pageSize))
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage
      }
    }
  },
  methods: {
    buildTrendCard(key, label, color, badgeBackground, isCurrency = false) {
      const values = this.trendDays.map(day => Number(day[key] || 0))
      const pointsData = this.buildTrendPoints(values)
      const latest = values[values.length - 1] || 0
      const previous = values[values.length - 2] || latest || 1
      const change = previous ? ((latest - previous) / previous) * 100 : 0
      return {
        key,
        label,
        color,
        fillColor: color,
        badgeBackground,
        latest: this.formatTrendValue(latest, isCurrency),
        changeLabel: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
        values: values.map(value => this.formatTrendValue(value, isCurrency)),
        points: pointsData.points,
        areaPath: pointsData.areaPath,
        dotPoints: pointsData.dotPoints
      }
    },
    buildTrendPoints(values) {
      if (!values.length) {
        return {
          points: '',
          areaPath: '',
          dotPoints: []
        }
      }
      const width = 360
      const height = 168
      const topPadding = 18
      const bottomPadding = 24
      const chartHeight = height - topPadding - bottomPadding
      const maxValue = Math.max(...values, 1)
      const stepX = values.length > 1 ? width / (values.length - 1) : width
      const dotPoints = values.map((value, index) => {
        const x = Number((index * stepX).toFixed(2))
        const y = Number((topPadding + chartHeight - (value / maxValue) * chartHeight).toFixed(2))
        return { x, y }
      })
      const points = dotPoints.map(point => `${point.x},${point.y}`).join(' ')
      const areaPath = `M 0 ${height - bottomPadding} L ${points} L ${width} ${height - bottomPadding} Z`
      return { points, areaPath, dotPoints }
    },
    formatTrendValue(value, isCurrency) {
      if (isCurrency) {
        return `¥${Number(value).toLocaleString()}`
      }
      return Number(value).toLocaleString()
    },
    handleSortChange({ column, prop, order }) {
      const sortMap = {
        impressions: 'impressions',
        clicks: 'clicks',
        conversions: 'conversions',
        ctr: 'ctr'
      }
      const columnKey = (column && column.columnKey) || prop
      const nextSort = sortMap[columnKey]
      if (!nextSort) {
        return
      }
      this.activeSort = nextSort
      this.sortOrder = order === 'ascending' ? 'ascending' : 'descending'
      this.currentPage = 1
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    getTopThreshold(rows, field, ratio) {
      if (!rows.length) {
        return 0
      }
      const sortedValues = rows
        .map(row => Number(row[field] || 0))
        .sort((a, b) => b - a)
      const index = Math.min(sortedValues.length - 1, Math.max(0, Math.floor(sortedValues.length * ratio)))
      return sortedValues[index]
    },
    showTrendTooltip(card, point, index) {
      const currentDay = this.trendDays[index] || {}
      this.activeTrendTooltip = {
        visible: true,
        cardKey: card.key,
        label: card.label,
        value: card.values[index] || '0',
        date: currentDay.date || '',
        x: Math.min(82, Math.max(12, (point.x / 360) * 100)),
        y: Math.max(18, point.y - 56),
        color: card.color
      }
    },
    hideTrendTooltip() {
      this.activeTrendTooltip.visible = false
    },
    async downloadActivityConversionLeads(activity) {
      if (!activity || !activity.activityId || !this.activeCustomer.id) {
        this.$message.error('当前活动线索暂不可下载')
        return
      }
      const url = `/reports/activities/${encodeURIComponent(activity.activityId)}/conversion-leads/export`
      try {
        const response = await this.$http.get(url, {
          params: {
            customer_id: this.activeCustomer.id
          },
          responseType: 'blob',
          validateStatus: () => true
        })
        const contentType = response.headers['content-type'] || ''
        if (contentType.includes('application/json')) {
          const message = await this.readBlobMessage(response.data)
          this.$message.error(message || '涉及数据安全，不可下载')
          return
        }
        if (response.status !== 200) {
          this.$message.error('当前活动线索暂不可下载')
          return
        }
        const disposition = response.headers['content-disposition'] || ''
        const fileName = this.extractDownloadFilename(disposition, activity.activityName || '活动转化线索.csv')
        this.downloadBlobFile(response.data, fileName)
      } catch (error) {
        this.$message.error('当前活动线索暂不可下载')
      }
    },
    async readBlobMessage(blob) {
      try {
        const text = await blob.text()
        const payload = JSON.parse(text)
        return payload.message || ''
      } catch (error) {
        return ''
      }
    },
    extractDownloadFilename(disposition, fallbackName) {
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        return decodeURIComponent(utf8Match[1])
      }
      const basicMatch = disposition.match(/filename="?([^"]+)"?/i)
      if (basicMatch && basicMatch[1]) {
        return basicMatch[1]
      }
      return `${fallbackName}.csv`.replace(/\.csv\.csv$/, '.csv')
    },
    downloadBlobFile(blob, fileName) {
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    },
    viewCampaignDetail(row) {
      if (!row || !row.id) {
        this.$message.error('当前计划详情不可查看')
        return
      }
      this.$router.push(`/campaigns/edit/${row.id}`)
    }
  }
}
</script>

<style scoped>
.reports-page {
  padding: 22px 0 30px;
}

.hero-panel {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px;
  border-radius: 28px;
  margin-bottom: 22px;
  background:
    radial-gradient(circle at top right, rgba(44, 115, 255, 0.28), transparent 28%),
    linear-gradient(135deg, #10203f 0%, #19356a 52%, #2857b8 100%);
  color: #f5f8ff;
  box-shadow: 0 24px 60px rgba(23, 52, 110, 0.22);
}

.hero-tag {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  margin-bottom: 14px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.hero-copy h2 {
  margin: 0 0 10px;
  font-size: 32px;
  line-height: 1.1;
}

.hero-copy p {
  max-width: 680px;
  margin: 0;
  color: rgba(245, 248, 255, 0.82);
  line-height: 1.7;
}

.hero-meta {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.meta-pill {
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.period-switcher {
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
}

.period-button {
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(245, 248, 255, 0.8);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.period-button.active {
  background: #ffffff;
  color: #1b3261;
  border-color: #ffffff;
  box-shadow: 0 8px 18px rgba(16, 32, 63, 0.18);
}

.meta-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: rgba(245, 248, 255, 0.72);
}

.summary-grid {
  margin-bottom: 22px;
}

.metric-card {
  min-height: 142px;
  padding: 24px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 16px 34px rgba(18, 40, 80, 0.08);
}

.metric-label {
  display: inline-block;
  margin-bottom: 14px;
  font-size: 13px;
  color: #6e7d99;
}

.metric-value {
  font-size: 30px;
  font-weight: 700;
  color: #13213f;
}

.metric-footnote {
  display: block;
  margin-top: 12px;
  color: #8190a8;
  font-size: 13px;
}

.trend-card {
  margin-bottom: 22px;
}

.trend-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.trend-panel {
  position: relative;
  padding: 18px 18px 14px;
  border-radius: 22px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef3fb 100%);
}

.trend-tooltip {
  position: absolute;
  z-index: 2;
  transform: translateX(-50%);
  min-width: 120px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(16, 24, 40, 0.92);
  color: #fff;
  box-shadow: 0 14px 28px rgba(10, 18, 32, 0.22);
  pointer-events: none;
}

.tooltip-label,
.tooltip-date {
  display: block;
  font-size: 12px;
}

.trend-tooltip strong {
  display: block;
  margin: 4px 0;
  font-size: 18px;
}

.trend-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.trend-label {
  display: block;
  margin-bottom: 8px;
  color: #70809a;
  font-size: 13px;
}

.trend-head strong {
  font-size: 28px;
  color: #13213f;
}

.trend-badge {
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.trend-svg {
  width: 100%;
  height: 168px;
  overflow: visible;
}

.trend-grid-line {
  fill: none;
  stroke: rgba(19, 33, 63, 0.09);
  stroke-width: 1;
}

.trend-line {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.trend-area {
  stroke: none;
}

.trend-dot {
  stroke: #fff;
  stroke-width: 2;
}

.trend-axis {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  color: #7e8ba1;
  font-size: 12px;
}

.trend-axis span {
  flex: 1 1 0;
}

.trend-axis span:last-child {
  text-align: right;
}

.trend-axis.single-point span {
  text-align: left;
}

.content-grid {
  margin-top: 2px;
}

.report-card {
  border: none;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 16px 34px rgba(18, 40, 80, 0.08);
}

.report-card ::v-deep .el-card__header {
  padding: 22px 24px 16px;
  border-bottom: 1px solid rgba(19, 33, 63, 0.08);
}

.report-card ::v-deep .el-card__body {
  padding: 18px 24px 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.section-header.compact {
  align-items: flex-start;
}

.section-copy {
  flex: 1 1 300px;
  min-width: 0;
}

.section-header h3 {
  margin: 0;
  font-size: 20px;
  color: #13213f;
}

.section-header p {
  margin: 6px 0 0;
  color: #7d8aa3;
  font-size: 13px;
}

.table-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  width: min(100%, 456px);
  max-width: 100%;
  min-width: 0;
  flex: 0 1 456px;
  margin-left: auto;
}

.search-box {
  width: 100%;
  padding-left: 0;
}

.search-input {
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(19, 33, 63, 0.1);
  border-radius: 12px;
  background: #fff;
  color: #13213f;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input:focus {
  border-color: rgba(47, 107, 255, 0.4);
  box-shadow: 0 0 0 4px rgba(47, 107, 255, 0.08);
}

.filter-switcher,
.sort-switcher {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.filter-button,
.sort-button {
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-button {
  border: 1px solid rgba(19, 33, 63, 0.08);
  background: #ffffff;
  color: #64748f;
}

.filter-button.active {
  background: #eef4ff;
  color: #2456d3;
  border-color: rgba(47, 107, 255, 0.18);
}

.report-table ::v-deep th {
  background: #f6f8fc;
  color: #5b6b88;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.action-icon-button {
  padding: 0;
  font-size: 16px;
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-cell span {
  min-width: 42px;
  color: #51617e;
}

.insight-card,
.rank-card {
  margin-bottom: 20px;
}

.content-side-col .report-card {
  min-height: 100%;
}

.insight-list,
.rank-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.lead-export-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lead-export-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef3fb 100%);
}

.lead-export-content strong {
  display: block;
  margin-bottom: 6px;
  color: #13213f;
}

.lead-export-content span {
  color: #72819b;
  font-size: 13px;
}

.lead-export-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  background: #2f6bff;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.lead-export-button:hover {
  background: #1f57e3;
}

.empty-state {
  padding: 18px;
  border-radius: 18px;
  background: #f6f8fc;
  color: #72819b;
  text-align: center;
}

.insight-item {
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7f9fd 0%, #eef3fb 100%);
}

.insight-item.warning {
  background: linear-gradient(180deg, #fff4ea 0%, #ffe7d1 100%);
}

.insight-kicker {
  display: inline-block;
  margin-bottom: 8px;
  color: #6f7f9a;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.insight-item strong {
  display: block;
  margin-bottom: 6px;
  color: #13213f;
}

.insight-item p {
  margin: 0;
  line-height: 1.6;
  color: #5f6f8b;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(19, 33, 63, 0.08);
}

.rank-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.rank-index {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2f6bff, #2db7ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.rank-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rank-content strong {
  color: #13213f;
}

.rank-content span,
.rank-value {
  color: #73829b;
  font-size: 13px;
}

@media (max-width: 1500px) {
  .table-controls {
    width: min(100%, 456px);
    flex-basis: 456px;
  }
}

@media (max-width: 1366px) {
  .section-copy,
  .table-controls {
    flex-basis: 100%;
    width: 100%;
  }

  .filter-switcher,
  .sort-switcher {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}

@media (max-width: 1200px) {
  .hero-panel {
    flex-direction: column;
  }

  .trend-grid {
    grid-template-columns: 1fr;
  }

  .table-controls {
    width: 100%;
    flex-basis: 100%;
  }

  .search-box {
    padding-left: 0;
  }

  .lead-export-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .lead-export-button {
    width: 100%;
  }
}
</style>
